"use client";

import { useCallback, useRef, useState } from "react";
import { Camera, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const ACCEPTED = ".pdf,.jpg,.jpeg,.png,.csv";

type Props = {
  files: File[];
  onFilesChange: (files: File[]) => void;
  disabled?: boolean;
};

export function UploadDropzone({ files, onFilesChange, disabled }: Props) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      const list = Array.from(incoming);
      const valid = list.filter((f) => {
        const n = f.name.toLowerCase();
        return n.endsWith(".pdf") || n.endsWith(".jpg") || n.endsWith(".jpeg") || n.endsWith(".png") || n.endsWith(".csv");
      });
      onFilesChange([...files, ...valid]);
    },
    [files, onFilesChange],
  );

  return (
    <div className="space-y-3">
      <div
        className={cn(
          "flex min-h-40 cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-6 transition-colors",
          dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/30",
          disabled && "pointer-events-none opacity-50",
        )}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="size-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Drag & drop invoices (PDF, JPG, PNG, CSV)</p>
        <p className="text-xs text-muted-foreground">Max 20 files, 50MB each</p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) addFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) addFiles(e.target.files);
          e.target.value = "";
        }}
      />

      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => inputRef.current?.click()}>
          <Upload className="mr-2 size-4" />
          Choose files
        </Button>
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => cameraRef.current?.click()}>
          <Camera className="mr-2 size-4" />
          Camera
        </Button>
        {files.length > 0 && (
          <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => onFilesChange([])}>
            Clear ({files.length})
          </Button>
        )}
      </div>

      {files.length > 0 && (
        <ul className="max-h-40 space-y-1 overflow-y-auto text-sm">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="truncate text-muted-foreground">
              {f.name} ({(f.size / 1024).toFixed(0)} KB)
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
